import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  Card,
  CardHeader,
  CardFooter,
  Button,
  Input,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import {
  updateCheckoutQuestion,
  getCheckoutQuestion,
} from "store/actions/checkoutQuestionAction";
import DeleteQuestionModal from "../Modals/DeleteQuestionModal";
import EditCheckoutModal from "components/Modals/EditCheckoutModal";
import Pagination from "components/shared/Pagination";
import TableComponent from "components/shared/Table";
import AddCheckoutQuestionModal from "components/Modals/AddCheckoutQuestionModal";
import tick from "assets/img/tick.png";
import cross from "assets/img/cros.png";

function CheckoutQuestionTable() {
  const { question, loading } = useSelector((state) => state.checkoutQuestion);
  const { user, userPermissions } = useSelector((state) => state.auth);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [ID, setID] = useState("");
  const [editQuestion, setEditQuestion] = useState({
    title: "",
    type: "",
    isMandatory: false,
  });
  const checkOutPermissions = userPermissions?.checkoutQuestions;
  const [questions, setQuestions] = useState([]);
  const dispatch = useDispatch();
  const [pageSize, setPageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState("");
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const history = useHistory();
  const paginatedQuestions = questions.slice(startIndex, endIndex);
  const [addModal, setAddModal] = useState(false);
  const totalPages = Math.ceil(questions.length / pageSize);

  const deleteToggle = () => {
    const updatedQuestions = questions.filter((q) => q.id !== ID);
    setQuestions(updatedQuestions);

    const totalItems = updatedQuestions.length;
    const totalPages = Math.ceil(totalItems / pageSize);

    if (currentPage > totalPages) {
      setCurrentPage(totalPages > 0 ? totalPages : 1);
    }
    setDeleteModal(!deleteModal);
    dispatch(getCheckoutQuestion(user?.restaurantID, filter));
  };

  const addToggle = () => {
    setAddModal(!addModal);
  };

  const handleAddQuestion = (newFeedback) => {
    const updatedQuestions = [
      ...questions,
      { ...newFeedback, id: newFeedback?.id || Date.now() },
    ];
    setQuestions(updatedQuestions);
    const totalPages = Math.ceil(updatedQuestions.length / pageSize);
    setCurrentPage(totalPages);
    dispatch(getCheckoutQuestion(user?.restaurantID, filter));
    addToggle();
  };

  const cancelToggle = () => {
    setDeleteModal(!deleteModal);
  };

  const editToggle = () => setEditModal(!editModal);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;

    const reorderedQuestions = Array.from(questions);
    const [removed] = reorderedQuestions.splice(source.index, 1);
    reorderedQuestions.splice(destination.index, 0, removed);

    const updatedQuestions = reorderedQuestions.map((question, index) => ({
      ...question,
      position: index,
    }));

    setQuestions(updatedQuestions);
    dispatch(updateCheckoutQuestion(updatedQuestions));
  };

  const handleFilterChange = (event) => {
    const selectedFilter = event.target.value;
    setFilter(selectedFilter);
    dispatch(getCheckoutQuestion(user?.restaurantID, selectedFilter));
  };

  useEffect(() => {
    setQuestions(question || []);
  }, [question]);

  useEffect(() => {
    if (question?.length) {
      setQuestions(
        question.map((q) => ({
          ...q,
          id: q.id || Date.now(),
        }))
      );
    }
  }, [question]);

  return (
    <Row>
      <div className="col">
        <Card className="shadow">
          <CardHeader className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center px-2 px-lg-4">
            <Col xs="12" lg="3" xl="6" className="mb-3 mb-lg-0">
              <h3 className="mb-0">Customer Feedback</h3>
            </Col>
            <Row className="w-100 mx-0">
              <Col xs="12" sm="4" lg="4" className="mb-3 mb-sm-0">
                <Input
                  value={filter}
                  onChange={handleFilterChange}
                  type="select"
                >
                  <option value="">Select Filter</option>
                  <option value="order">Order</option>
                  <option value="marketing">Marketing</option>
                </Input>
              </Col>
              <Col xs="12" sm="4" lg="4" className="mb-3 mb-sm-0">
                <Input
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  type="select"
                >
                  <option value="5">Show Entries</option>
                  <option value="10">10</option>
                  <option value="15">15</option>
                  <option value="20">20</option>
                </Input>
              </Col>
              <Col xs="12" sm="4" lg="4">
                {(user?.type === "restaurant" || checkOutPermissions?.add) && (
                  <Button
                    color="primary"
                    onClick={addToggle}
                    filter={filter}
                    block
                  >
                    Add
                  </Button>
                )}
              </Col>
            </Row>
          </CardHeader>

          {user?.type === "kitchen-admin" && !checkOutPermissions?.get ? (
            <Row className="py-4 justify-content-center align-items-center">
              You don't have permission to access this page.
            </Row>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              {loading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <Spinner size="md" color="primary" />
                </div>
              ) : (
                <TableComponent responsive>
                  <thead className="thead-light">
                    <tr>
                      <th scope="col"></th>
                      <th scope="col">Questions</th>
                      <th scope="col">Type</th>
                      <th scope="col">IsMandatory</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <Droppable droppableId="questionList">
                    {(provided) => (
                      <tbody
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        {paginatedQuestions.length > 0 ? (
                          paginatedQuestions.map((question, index) => (
                            <Draggable
                              key={question.id || index}
                              draggableId={question.id || index.toString()}
                              index={index}
                            >
                              {(provided) => (
                                <tr
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  <td>
                                    {" "}
                                    <div className="d-flex align-items-center h-100">
                                      <i className="fas fa-2x fa-ellipsis-v"></i>
                                      <i className="fas fa-2x fa-ellipsis-v"></i>
                                      <i className="fas fa-2x fa-ellipsis-v"></i>
                                    </div>
                                  </td>
                                  <td>{question.title}</td>
                                  <td>{question.type}</td>
                                  {question.isMandatory ? (
                                    <td>
                                      <img
                                        src={tick}
                                        alt="Png"
                                        style={{
                                          height: "35px",
                                          width: "35px",
                                        }}
                                      />
                                    </td>
                                  ) : (
                                    <td>
                                      <img
                                        src={cross}
                                        alt="N/A"
                                        style={{
                                          height: "35px",
                                          width: "35px",
                                        }}
                                      />
                                    </td>
                                  )}
                                  <td>
                                    {(user?.type === "restaurant" ||
                                      checkOutPermissions?.update) && (
                                      <Button
                                        className="btn-sm"
                                        color="primary"
                                        onClick={() => {
                                          setEditQuestion(question);
                                          editToggle();
                                        }}
                                      >
                                        Edit
                                      </Button>
                                    )}
                                    <Button
                                      className="btn-sm"
                                      color="success"
                                      onClick={() =>
                                        history.push(
                                          `/admin/checkout-responses/${question.id}`
                                        )
                                      }
                                    >
                                      View Response
                                    </Button>
                                    {(user?.type === "restaurant" ||
                                      checkOutPermissions?.delete) && (
                                      <Button
                                        className="btn-sm"
                                        color="danger"
                                        onClick={() => {
                                          setID(question.id);
                                          cancelToggle();
                                        }}
                                      >
                                        Delete
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </Draggable>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center py-5">
                              No Question Added
                            </td>
                          </tr>
                        )}
                        {provided.placeholder}
                      </tbody>
                    )}
                  </Droppable>
                </TableComponent>
              )}
              <DeleteQuestionModal
                modal={deleteModal}
                confirmToggle={deleteToggle}
                cancelToggle={cancelToggle}
                id={ID}
              />
              <EditCheckoutModal
                editToggle={editToggle}
                editModal={editModal}
                editQuestion={editQuestion}
                setEditQuestion={setEditQuestion}
              />

              <AddCheckoutQuestionModal
                addModal={addModal}
                addToggle={addToggle}
                onAdd={handleAddQuestion}
              />
            </DragDropContext>
          )}

          <CardFooter className="py-4">
            <Pagination
              currentPage={currentPage - 1}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page + 1)}
            />
          </CardFooter>
        </Card>
      </div>
    </Row>
  );
}

export default CheckoutQuestionTable;
